import { forwardRef, RefObject } from 'react';

import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Spinner,
  Stack,
} from '@chakra-ui/react';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { useCreateEmployeeStore } from 'app/store';

import { employeeDetailsFormSchema } from 'app/utils/schemas';

import useFetchPayCalendarsForEmployee from 'app/hooks/useFetchPayCalendarsForEmployee';

import {
  ArrowLeftOutline,
  ArrowRightOutline,
  CrossOutline,
  CustomAlert,
} from '@aslanpay/portal-common-lib';

import { useParams } from 'react-router-dom';
import useAuthContext from 'app/hooks/useAuthContext';

import { EmployeeCompanyFormFields, EmployeeDetailsFormFields, EmployeeFormProps } from '../types';

const EmployeeDetailsForm = forwardRef<HTMLElement, EmployeeFormProps>(
  ({ id, onStepAction, previousStep, canPrevStep }, ref) => {
    const employeeData = useCreateEmployeeStore((state) => state.employeeData);
    const setEmployeeData = useCreateEmployeeStore((state) => state.setEmployeeData);

    const { companyId: urlCompanyId } = useParams();
    const { user } = useAuthContext();
    const { companyId: storeCompanyId } = employeeData as EmployeeCompanyFormFields;

    // Use companyId from store, URL params, or user context as fallback
    const companyId = storeCompanyId || urlCompanyId || user?.companyId;

    const {
      register,
      handleSubmit,
      watch,
      resetField,
      setFocus,
      getValues,
      formState: { errors, isValid },
    } = useForm<EmployeeDetailsFormFields>({
      mode: 'onBlur',
      defaultValues: employeeData as EmployeeDetailsFormFields,
      resolver: yupResolver(employeeDetailsFormSchema),
    });

    const watchFirstName = watch('firstName');
    const watchLastName = watch('lastName');
    const watchEmail = watch('email');
    const watchAdditionalEmail = watch('additionalEmail');

    const handleNextStep = (data: EmployeeDetailsFormFields) => {
      setEmployeeData(data);
      onStepAction();
    };

    const resetAndFocusField = (field: keyof EmployeeDetailsFormFields) => {
      resetField(field, { defaultValue: '' });
      setFocus(field);
    };

    const { data: payCalendars, isLoading: isLoadingPayCalendars } =
      useFetchPayCalendarsForEmployee([companyId || '']);

    const handleBackCLick = () => {
      const formValues = getValues();
      setEmployeeData(formValues);
      previousStep();
    };

    return isLoadingPayCalendars ? (
      <Center width="full" height="full" flexGrow={1}>
        <Spinner color="brand.purple" emptyColor="brand.purpleAlpha30" size="xl" />
      </Center>
    ) : (
      <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
        <Stack spacing="20px">
          {!payCalendars?.list.length && (
            <CustomAlert
              variant="simple"
              status="warning"
              title="You cannot create an employee on a company that does not have a pay calendar associated. Make sure that you have at least one pay calendar created for the current company."
            />
          )}
          <HStack spacing="20px" alignItems="flex-start">
            <FormControl isInvalid={!!errors?.firstName?.message} isRequired>
              <FormLabel>First name</FormLabel>
              <InputGroup>
                <Input type="text" placeholder="Enter first name" {...register('firstName')} />
                {watchFirstName && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Clear first name"
                      icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                      variant="link"
                      onClick={() => resetAndFocusField('firstName')}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage>{errors?.firstName?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors?.lastName?.message} isRequired>
              <FormLabel>Last name</FormLabel>
              <InputGroup>
                <Input type="text" placeholder="Enter last name" {...register('lastName')} />
                {watchLastName && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Clear last name"
                      icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                      variant="link"
                      onClick={() => resetAndFocusField('lastName')}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage>{errors?.lastName?.message}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isInvalid={!!errors?.email?.message} isRequired>
            <FormLabel>Main email</FormLabel>
            <InputGroup>
              <Input type="text" placeholder="Enter main email" {...register('email')} />
              {watchEmail && (
                <InputRightElement>
                  <IconButton
                    aria-label="Clear email"
                    icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                    variant="link"
                    onClick={() => resetAndFocusField('email')}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.additionalEmail?.message}>
            <FormLabel>Additional email</FormLabel>
            <InputGroup>
              <Input
                type="text"
                placeholder="Enter additional email"
                {...register('additionalEmail')}
              />
              {watchAdditionalEmail && (
                <InputRightElement>
                  <IconButton
                    aria-label="Clear additional email"
                    icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                    variant="link"
                    onClick={() => resetAndFocusField('additionalEmail')}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            <FormErrorMessage>{errors?.additionalEmail?.message}</FormErrorMessage>
          </FormControl>

          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canPrevStep}
              leftIcon={<ArrowLeftOutline boxSize="20px" />}
              onClick={handleBackCLick}
              mr="16px"
            >
              Back
            </Button>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid || !payCalendars?.list.length}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Next
            </Button>
          </Portal>
        </Stack>
      </form>
    );
  }
);

EmployeeDetailsForm.displayName = 'EmployeeDetailsForm';

export default EmployeeDetailsForm;
